import DragDropResizableHandleOptions from "../interfaces/DragDropResizableHandleOptions";
import ItemHTMLElement from "../interfaces/ItemHTMLElement";
import { isTouch, touchstart, pointerdown, touchmove, touchend } from "../utils/touchUtils";

/** 
 * @internal 
 * Class for a drag drop resizable handle
 */
export default class DragDropResizableHandle
{
  /** 
   * @internal 
   * The element of the handle
   */
  protected el: HTMLElement;
  /**
   * @internal 
   * True after we've moved enough pixels to start a resize 
   */
  protected moving = false;

  /** 
   * @internal 
   * The mouse down event
   */
  protected mouseDownEvent: MouseEvent;
  /** 
   * @internal 
   * Prefix for the class names of the handle
   */
  protected static prefix = 'ui-resizable-';

  /** 
   * Constructor
   * @param {ItemHTMLElement} host The host element
   * @param {string} dir The direction of the handle
   * @param {DragDropResizableHandleOptions} option The options for the handle
   */
  constructor (protected host: ItemHTMLElement, protected dir: string, protected option: DragDropResizableHandleOptions)
  {
    // create var event binding so we can easily remove and still look like TS methods (unlike anonymous functions)
    this._mouseDown = this._mouseDown.bind(this);
    this._mouseMove = this._mouseMove.bind(this);
    this._mouseUp = this._mouseUp.bind(this);
    this._keyEvent = this._keyEvent.bind(this);

    this._init();
  }

  /** 
   * @internal 
   * Initialize the handle
   * @returns {DragDropResizableHandle}
   */
  protected _init (): DragDropResizableHandle
  {
    const el = this.el = document.createElement('div');
    el.classList.add('ui-resizable-handle');
    el.classList.add(`${ DragDropResizableHandle.prefix }${ this.dir }`);
    el.style.zIndex = '100';
    el.style.userSelect = 'none';
    this.host.appendChild(this.el);
    this.el.addEventListener('mousedown', this._mouseDown);
    if (isTouch)
    {
      this.el.addEventListener('touchstart', touchstart);
      this.el.addEventListener('pointerdown', pointerdown);
    }
    return this;
  }

  /** 
   * Call this when resize handle needs to be removed and cleaned up 
   * @returns {DragDropResizableHandle}
   */
  public destroy (): DragDropResizableHandle
  {
    if (this.moving) 
      this._mouseUp(this.mouseDownEvent);

    this.el.removeEventListener('mousedown', this._mouseDown);
    if (isTouch)
    {
      this.el.removeEventListener('touchstart', touchstart);
      this.el.removeEventListener('pointerdown', pointerdown);
    }
    this.host.removeChild(this.el);
    delete this.el;
    delete this.host;
    return this;
  }

  /** 
   * @internal 
   * Called on mouse down on us: capture move on the entire document (mouse might not stay on us) until we release the mouse 
   * @param {MouseEvent} e The mouse event
   */
  protected _mouseDown (e: MouseEvent): void
  {
    this.mouseDownEvent = e;
    document.addEventListener('mousemove', this._mouseMove, { capture: true, passive: true }); // capture, not bubble
    document.addEventListener('mouseup', this._mouseUp, true);
    if (isTouch)
    {
      this.el.addEventListener('touchmove', touchmove);
      this.el.addEventListener('touchend', touchend);
    }
    e.stopPropagation();
    e.preventDefault();
  }

  /** 
   * @internal 
   * Called when mouse is moving - if we've moved enough to start, call start event, then move event
   * @param {MouseEvent} e The mouse event
   */
  protected _mouseMove (e: MouseEvent): void
  {
    let s = this.mouseDownEvent;
    if (this.moving)
    {
      this._triggerEvent('move', e);
    } else if (Math.abs(e.x - s.x) + Math.abs(e.y - s.y) > 2)
    {
      // don't start unless we've moved at least 3 pixels
      this.moving = true;
      this._triggerEvent('start', this.mouseDownEvent);
      this._triggerEvent('move', e);
      // now track keyboard events to cancel
      document.addEventListener('keydown', this._keyEvent);
    }
    e.stopPropagation();
  }

  /** 
   * @internal 
   * Called when mouse is released - stop tracking mouse move and mouse up events
   * @param {MouseEvent} e The mouse event
   */
  protected _mouseUp (e: MouseEvent): void
  {
    if (this.moving)
    {
      this._triggerEvent('stop', e);
      document.removeEventListener('keydown', this._keyEvent);
    }

    document.removeEventListener('mousemove', this._mouseMove, true);
    document.removeEventListener('mouseup', this._mouseUp, true);

    if (isTouch)
    {
      this.el.removeEventListener('touchmove', touchmove);
      this.el.removeEventListener('touchend', touchend);
    }

    delete this.moving;
    delete this.mouseDownEvent;
    e.stopPropagation();
    e.preventDefault();
  }

  /** 
   * @internal 
   * Called when keys are being pressed - use Esc to cancel 
   * @param {KeyboardEvent} e The keyboard event
   */
  protected _keyEvent (e: KeyboardEvent): void
  {
    if (e.key === 'Escape')
    {
      this.host.tileLayoutNode?.tileLayout?.engine.restoreInitial();
      this._mouseUp(this.mouseDownEvent);
    }
  }

  /** 
   * @internal 
   * Trigger an event
   * @param {string} name The event name
   * @param {MouseEvent} event The event object
   * @returns {DragDropResizableHandle}
   */
  protected _triggerEvent (name: string, event: MouseEvent): DragDropResizableHandle
  {
    if (this.option[ name ]) 
      this.option[ name ](event);

    return this;
  }
}
